import { IFutureSecurity } from "@/lib/interfaces and Enums/ENUMFutureSecurity";
import { IPortfolio } from "@/lib/interfaces and Enums/ENUMPortfolio";
import { TradeDirection } from "@/lib/interfaces and Enums/ENUMTradeDirection";
import {
  IFutureTradeMessage,
  SecurityIdentifierType,
  TradeMessageDirection,
  TradeMessageSubType,
  TradeMessageType,
} from "@/lib/interfaces and Enums/ENUMBondTradeMessage";
import { returnSettlementDateForDays } from "@/lib/businessRules/settlementDateRules";
import { formatNumberWithCommas } from "@/lib/utilities";
import { FutureTicketDirection, IFutureTicket } from "@/store/tradeFromScratchStore";
import {
  ITicketIssue,
  returnAllocationIssues,
  returnWireAllocations,
} from "./allocationRules";
import {
  ITicketField,
  returnRequiredTicketKeys,
  returnTicketFieldIssues,
  returnTransactionNumberDetail,
} from "./ticketSharedHelper";

// LQC-2494 / LQC-2495 - pure rules for the future ticket: what is still missing, what
// can be derived from the chosen contract, and the outbound message. No React, no store
// reads - the caller passes the resolved contract and portfolio in, so all of this is
// unit testable. Mirrors bondTicketHelper.

// The fields a user must fill before the ticket can be sent, in the order they appear
// in the ticket. Labels are what the Send button reports as missing.
export const FUTURE_TICKET_REQUIRED_FIELDS: ITicketField<IFutureTicket>[] = [
  { key: "bloombergSymbol", label: "Contract" },
  { key: "portfolioCode", label: "Portfolio" },
  { key: "contractCount", label: "Contracts", isNumeric: true },
  { key: "direction", label: "Direction" },
  { key: "settlementDate", label: "Settlement Date", isDate: true },
  { key: "tradeDate", label: "Trade Date", isDate: true },
];

// What the ticket marks with a "*". Derived from the list above so the two cannot
// drift apart.
export const FUTURE_TICKET_REQUIRED_KEYS = returnRequiredTicketKeys(
  FUTURE_TICKET_REQUIRED_FIELDS,
);

// Contract is one choice behind two rows (symbol and name), so bloombergSymbol standing
// in for both is deliberate - they are only ever set together.
// Splits are in contracts, like the trade itself.
export function returnMissingFutureTicketEntries(ticket: IFutureTicket): ITicketIssue[] {
  return [
    ...returnTicketFieldIssues(ticket, FUTURE_TICKET_REQUIRED_FIELDS),
    ...returnAllocationIssues(ticket.contractCount, ticket.additionalAllocations),
  ];
}

export function returnMissingFutureTicketFields(
  ticket: IFutureTicket,
): string[] {
  return returnMissingFutureTicketEntries(ticket).map(({ label }) => label);
}

export function isFutureTicketComplete(ticket: IFutureTicket): boolean {
  return returnMissingFutureTicketFields(ticket).length === 0;
}

// The two directions a future ticket can take. The enum other members are bond, swap
// and swaption vocabulary.
export const FUTURE_TICKET_DIRECTIONS: TradeDirection[] = [
  TradeDirection.BuyFuture,
  TradeDirection.SellFuture,
];

// Futures settle T+0 when the contract carries no SettlementDays (spec F1), not the bond T+2.
export const DEFAULT_FUTURE_SETTLEMENT_DAYS = 0;

// Picking (or clearing) the contract. A pick re-defaults settlement from its SettlementDays
// (LQC-2995); the user can still edit it after. Clearing leaves the date alone.
export function returnFutureContractChanges(
  tradeDate: string,
  contract: IFutureSecurity | undefined,
  bloombergSymbol: string,
): Partial<IFutureTicket> {
  if (!contract) return { bloombergSymbol };
  return {
    bloombergSymbol,
    settlementDate: returnSettlementDateForDays(
      tradeDate,
      contract.settlementDays ?? DEFAULT_FUTURE_SETTLEMENT_DAYS,
    ),
  };
}

// The ticket displays "Buy Future" but the message sends "Buy": FutureModelService
// validates through the shared ValidateDirection, which accepts Buy and Sell only.
// Sending "BuyFuture" is rejected even though the API enum defines it (LQC-2497).
export function returnFutureWireDirection(
  direction: FutureTicketDirection,
): TradeMessageDirection | "" {
  if (direction === TradeDirection.BuyFuture) return TradeMessageDirection.Buy;
  if (direction === TradeDirection.SellFuture) {
    return TradeMessageDirection.Sell;
  }
  return "";
}

// Indicative notional: contracts x contract amount. Not sent - the message carries the
// contract count. Null whenever the reference row has no published amount.
export function returnIndicativeContractNotional(
  contractCount: number | null,
  contractAmount: number | null | undefined,
): number | null {
  if (contractCount === null || contractAmount === null) return null;
  if (contractAmount === undefined) return null;
  return contractCount * contractAmount;
}

// What a contract reads as in the picker. Security.SecurityName is the ticker for every
// futures row we have seen (fn_GetBlockName builds futures block names straight off it),
// so showing both would just repeat the symbol - but a row that does carry a descriptive
// name still shows it rather than hiding behind the code.
export function returnContractOptionLabel(security: IFutureSecurity): string {
  const { bloombergSymbol, securityName } = security;
  if (!securityName || securityName === bloombergSymbol) return bloombergSymbol;
  return `${bloombergSymbol} - ${securityName}`;
}

export function returnContractAmountDisplay(
  security: IFutureSecurity | undefined,
  emptyValue: string,
): string {
  if (!security || security.contractAmount === null) return emptyValue;
  return formatNumberWithCommas(security.contractAmount);
}

// What the confirmation line describes. Built at send time - the ticket is cleared the
// moment the POST succeeds, so nothing can be read back off it afterwards.
export function returnSentFutureTicketSummary(ticket: IFutureTicket): string {
  const contracts =
    typeof ticket.contractCount === "number" && ticket.contractCount > 0
      ? `${formatNumberWithCommas(ticket.contractCount)} x`
      : "";
  return [ticket.direction, contracts, ticket.bloombergSymbol]
    .filter(Boolean)
    .join(" ");
}

export interface IBuildFutureTradeMessageParams {
  ticket: IFutureTicket;
  portfolio: IPortfolio | undefined;
  traderName: string;
  // Used only when the portfolio row carries no customerCode.
  fallbackEntity?: string;
}

// Returns undefined when the ticket cannot legally be sent, so the caller never has to
// decide what a half-built message means.
export function buildFutureTradeMessage({
  ticket,
  portfolio,
  traderName,
  fallbackEntity = "",
}: IBuildFutureTradeMessageParams): IFutureTradeMessage | undefined {
  if (!isFutureTicketComplete(ticket)) return undefined;
  if (!portfolio) return undefined;
  const direction = returnFutureWireDirection(ticket.direction);
  if (!direction) return undefined;
  // The contract count travels in the nominal slot - the endpoint has no separate field.
  const nominal = ticket.contractCount as number;

  return {
    messageType: TradeMessageType.Trade,
    subType: TradeMessageSubType.Future,
    clientDetails: {
      originatingEntity: portfolio.customerCode || fallbackEntity,
      lei: portfolio.lei,
      traderName,
    },
    security: {
      identifierDetails: [
        {
          identifier: ticket.bloombergSymbol,
          identifierType: SecurityIdentifierType.BloombergSymbol,
        },
      ],
    },
    tradeDetails: {
      tradeDate: ticket.tradeDate,
      direction,
      nominal,
      settlementDate: ticket.settlementDate,
      ...returnTransactionNumberDetail(ticket.transactionNumber),
    },
    allocations: returnWireAllocations(
      portfolio.portfolioCode,
      nominal,
      ticket.additionalAllocations,
    ),
  };
}
